import _ from 'lodash'
import { useState, MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from '@apollo/client'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Modal, Table } from 'antd'
import { getNonconfigurableOrderPartConfigInfo_getNonconfigurableOrderPartConfigInfo as NonconfigurableOrderPartConfigInfoType } from '@Orders/buttons/ReorderButton/__generated__/getNonconfigurableOrderPartConfigInfo'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Link from '~/core/Link'
import Button from '~/core/Button'
import getColor from '~/lib/getColor'
import { openChatDriftReorderGeneral } from '~/redux-modules/drift'
import { getUserIdentity } from '~/redux-modules/identity'

import duplicateQuoteFromOrderMutation from './duplicateQuoteFromOrderMutation'
import { duplicateQuoteFromOrder as DuplicateQuoteFromOrderMutationType } from './__generated__/duplicateQuoteFromOrder'

const PLACEMENT = 'show-invalid-config-modal'
const SCROLL_Y = 180

interface ManufacturingOptionsType {
    processes: NonconfigurableOrderPartConfigInfoType['processes']
    materials: NonconfigurableOrderPartConfigInfoType['materials']
    postProcesses: NonconfigurableOrderPartConfigInfoType['postProcesses']
    secondaryProcesses: NonconfigurableOrderPartConfigInfoType['secondaryProcesses']
}

export default function InvalidConfigModal(props: {
    nonConfigurableInfo: NonconfigurableOrderPartConfigInfoType[]
    onClose?: (evt: MouseEvent<HTMLElement>) => void
    orderName: string
    visible: boolean
}) {
    const { visible, onClose, orderName, nonConfigurableInfo } = props

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const identity = useSelector(getUserIdentity)

    const [isLoading, setLoading] = useState(false)

    const [duplicateQuote] = useMutation<DuplicateQuoteFromOrderMutationType>(
        duplicateQuoteFromOrderMutation,
    )

    const openChat = (evt: Event) => {
        evt.preventDefault()
        dispatch(openChatDriftReorderGeneral(identity.id))
    }

    const onProceed = async () => {
        setLoading(true)
        const response = await duplicateQuote({
            variables: {
                orderName,
            },
        })

        const quoteId = response?.data?.duplicateQuoteFromOrder.id

        setLoading(false)

        return navigate(`/pages/quotes/${quoteId}`)
    }

    const renderManufacturingOptions = (
        manufacturingOptions: ManufacturingOptionsType,
    ) => {
        const { materials, processes, postProcesses, secondaryProcesses } =
            manufacturingOptions

        return (
            <Box direction="col">
                {materials?.map(material => (
                    <Text key={material.id}>Material: {material.name}</Text>
                ))}
                {processes?.map(process => (
                    <Text key={process.id}>Process: {process.name}</Text>
                ))}
                {postProcesses?.map(postProcess => (
                    <Text key={postProcess.id}>
                        Post Processes: {postProcess.name}
                    </Text>
                ))}
                {secondaryProcesses?.map(secondaryProcess => (
                    <Text key={secondaryProcess.id}>
                        Secondary Processes: {secondaryProcess.name}
                    </Text>
                ))}
            </Box>
        )
    }

    const columns = [
        {
            title: 'Part',
            dataIndex: 'fileName',
            key: 'fileName',
            ellipsis: true,
        },
        {
            title: 'Manufacturing options',
            dataIndex: 'manufacturingOptions',
            key: 'manufacturingOptions',
            render: renderManufacturingOptions,
        },
    ]

    const tableData = nonConfigurableInfo.map(
        ({
            fileName,
            processes,
            materials,
            postProcesses,
            secondaryProcesses,
        }) => ({
            fileName,
            manufacturingOptions: {
                processes,
                materials,
                postProcesses,
                secondaryProcesses,
            },
        }),
    )

    return (
        <Modal
            visible={visible}
            footer={null}
            bodyStyle={{ padding: '26px' }}
            onCancel={onClose}
            width="600px"
        >
            <Box>
                <ExclamationCircleOutlined
                    style={{
                        color: getColor('warning'),
                        fontSize: '21px',
                        marginRight: '18px',
                    }}
                />
                <Text fontSize="16px">
                    The following manufacturing option(s) is no longer
                    available. After clicking “Proceed”, you will be prompted to
                    reconfigure your parts. You can also{' '}
                    <Link
                        name="chat-with-us"
                        placement={PLACEMENT}
                        onClick={openChat}
                    >
                        message us{' '}
                    </Link>
                    if you need our assistance.
                </Text>
            </Box>
            <Box
                color={getColor('blackAlpha65')}
                margin="25px"
                maxWidth="511px"
            >
                <Table
                    rowKey={record => _.uniqueId(record.fileName)}
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    scroll={{ y: SCROLL_Y }}
                    size="small"
                />
            </Box>
            <Box>
                <Button
                    name="close-invalid-config-modal"
                    placement={PLACEMENT}
                    size="large"
                    style={{ marginLeft: 'auto' }}
                    onClick={onClose}
                    data-test-target="invalid-config-modal-cancel-button"
                >
                    Cancel
                </Button>
                <Button
                    type="primary"
                    name="proceed-invalid-config-modal"
                    placement={PLACEMENT}
                    size="large"
                    style={{ marginLeft: '5px' }}
                    data-test-target="invalid-config-modal-proceed-button"
                    onClick={onProceed}
                    loading={isLoading}
                >
                    Proceed
                </Button>
            </Box>
        </Modal>
    )
}
